import ClapsIcon from 'assets/icons/new-claps.svg?react';
import { ChangeEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Button,
  analyticsClient,
  Slider,
  useNotification,
  TNotification,
} from '@spotted-zebra-uk/ui-components';
import { useSubmitAiInterviewFeedbackMutation } from '../../../generated/graphql';
import styles from './ThankYou.module.scss';
import { HEAP_ANALYTICS_EVENTS } from '@/constants/analytics';
import { debounce } from 'lodash';
import { ApolloError } from '@apollo/client';

interface ThankYouProps {
  candidateId: string;
  /** Where "Continue" exits to; defaults to the roles list. */
  exitUrl?: string;
  onSubmit?: () => void;
}

export function ThankYou({ candidateId, exitUrl, onSubmit }: ThankYouProps) {
  const { t } = useTranslation();
  const [feedback, setFeedback] = useState('');
  const [satisfactionScore, setSatisfactionScore] = useState<
    number | undefined
  >(undefined);
  const [error, setError] = useState<string | null>(null);

  const { handleMsgType } = useNotification();

  const [submitAiInterviewFeedbackMutation, { loading, data }] =
    useSubmitAiInterviewFeedbackMutation();

  function handleSubmit() {
    if (!satisfactionScore) {
      setError(t('aiInterview.thankYou.requiredField'));
      return;
    }

    setError(null);

    submitAiInterviewFeedbackMutation({
      variables: {
        id: candidateId,
        feedback: {
          text: feedback,
          score: satisfactionScore,
        },
      },
      onError: (e: ApolloError) => {
        handleMsgType({
          type: TNotification.error,
          title: e.name,
          message: e.message || '',
        });
      },
    });

    analyticsClient.trackEvent({
      name: HEAP_ANALYTICS_EVENTS.CANDIDATE_AI_INTERVIEW_FEEDBACK_SUBMIT_BUTTON_CLICK,
    });

    onSubmit?.();
  }

  function handleTextChange(e: ChangeEvent<HTMLTextAreaElement>) {
    setFeedback(e.target.value);
    debouncedTextChangeTracking(e.target.value);
  }

  function handleSliderChange(value: number | undefined) {
    if (value !== undefined) {
      setSatisfactionScore(value);
      // Clear error when user interacts with slider
      if (error) {
        setError(null);
      }

      analyticsClient.trackEvent({
        name: HEAP_ANALYTICS_EVENTS.CANDIDATE_AI_INTERVIEW_FEEDBACK_SLIDER_CHANGE,
        properties: {
          score: value,
        },
      });
    }
  }

  useEffect(() => {
    analyticsClient.trackEvent({
      name: HEAP_ANALYTICS_EVENTS.CANDIDATE_AI_INTERVIEW_FEEDBACK_PAGE_VISIT,
    });
  }, []);

  const isFeedbackSubmitted = data?.submitAiInterviewFeedback?.id;

  return (
    <div className={styles.thankYou}>
      <div className={styles.thankYouContent}>
        <div className={styles.thankYouHeader}>
          <div className={styles.thankYouIconWrapper}>
            <div className={styles.thankYouIconCircle}>
              <ClapsIcon className={styles.thankYouIcon} />
            </div>
          </div>
          <h1 className={styles.thankYouTitle}>
            {t('aiInterview.thankYou.title')}
          </h1>
          <p className={styles.thankYouSubtitle}>
            {t('aiInterview.thankYou.subtitle')}
          </p>
        </div>

        {!isFeedbackSubmitted ? (
          <>
            <div className={styles.sliderSection}>
              <label
                htmlFor="satisfaction-score"
                className={styles.sliderLabel}
              >
                {t('aiInterview.thankYou.satisfactionLabel')}
              </label>
              <Slider
                id="satisfaction-score"
                value={satisfactionScore}
                onChange={handleSliderChange}
                min={1}
                max={5}
                step={1}
                ariaLabel={t('aiInterview.thankYou.satisfactionAriaLabel')}
                marks={{
                  1: { label: t('aiInterview.thankYou.notSatisfied') },
                  2: { label: '' },
                  3: { label: t('aiInterview.thankYou.neutral') },
                  4: { label: '' },
                  5: { label: t('aiInterview.thankYou.verySatisfied') },
                }}
                className={styles.slider}
              />
              {error && (
                <div
                  className={styles.sliderError}
                  role="alert"
                  aria-live="polite"
                >
                  {error}
                </div>
              )}
            </div>

            <div className={styles.feedbackSection}>
              <label htmlFor="feedback" className={styles.feedbackLabel}>
                {t('aiInterview.thankYou.improvementsLabel')}
              </label>
              <div className={styles.textareaWrapper}>
                <textarea
                  id="feedback"
                  className={styles.feedbackTextarea}
                  placeholder={t('aiInterview.thankYou.improvementsPlaceholder')}
                  value={feedback}
                  onChange={handleTextChange}
                  rows={5}
                />
              </div>
            </div>

            <Button
              onClick={handleSubmit}
              className={styles.submitButton}
              disabled={loading}
              loading={loading}
            >
              {loading ? t('common.submitting') : t('common.submit')}
            </Button>
          </>
        ) : (
          <Button to={exitUrl ?? '/roles'} component={Link}>
            {t('common.continue')}
          </Button>
        )}
      </div>
    </div>
  );
}

const debouncedTextChangeTracking = debounce(trackTextChange, 3000);

function trackTextChange(text: string) {
  analyticsClient.trackEvent({
    name: HEAP_ANALYTICS_EVENTS.CANDIDATE_AI_INTERVIEW_FEEDBACK_TEXT_EDIT,
    properties: {
      text,
    },
  });
}

export default ThankYou;
